import React, { ChangeEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogInIcon, KeyRoundIcon } from "lucide-react";
import { Button } from "react-bulma-components";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../context/AuthContext";
import api from "../hooks/api";
import { API_URLS } from "../App";

type Stage = "credentials" | "mfa" | "enroll";

interface EnrollInfo {
  enrollToken: string;
  secret: string;
  otpauthUri: string;
  issuer: string;
  account: string;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, setProfile } = useAuth();

  const [stage, setStage] = useState<Stage>("credentials");
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [enroll, setEnroll] = useState<EnrollInfo | null>(null);
  const [code, setCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);

  const [loginRequest, setLoginRequest] = useState<LoginRequest>({
    username: "",
    password: "",
  });

  const handleError = (error: any | null) => {
    if (!error) {
      setError(null);
      return;
    }
    if (error.response?.status === 401) {
      setError(
        "Acesso não autorizado or expirado. Por favor, faça login novamente.",
      );
    } else if (error.response?.status === 422) {
      setError("Dados inválidos. Por favor, verifique suas credenciais.");
    } else if (error.response?.status === 403) {
      setErrorTitle("Acesso proibido.");
      setError("Por favor, contacte o suporte.");
    } else if (error.response?.status === 423) {
      const cookieLock = error.response?.data?.profile?.username || "unknown";
      setErrorTitle("Sessão Bloqueada");
      setError(
        "Por favor, contacte o suporte para desbloquear o device: " +
          cookieLock,
      );
    } else if (error.response?.status === 500) {
      setError(
        "Erro interno do servidor. Por favor, tente novamente mais tarde.",
      );
    } else if (
      error.response?.status === 503 ||
      error.response?.status === 502
    ) {
      setError("Serviço indisponível. Por favor, tente novamente mais tarde.");
    } else if (error.response?.status === 504) {
      setError(
        "Tempo limite de conexão excedido. Por favor, tente novamente mais tarde.",
      );
    } else {
      setError(error?.message);
    }
  };

  const completeLogin = (data: ProfileAndSettings) => {
    setProfile(data);
    let goToUrl = location.state?.from || "/";
    if (goToUrl === "/login") {
      goToUrl = "/";
    }
    navigate(goToUrl, { replace: true });
  };

  const handleCredentials = async (e: any) => {
    e.preventDefault();
    if (!loginRequest.username || !loginRequest.password) {
      setError("username and password são obrigatórios");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(API_URLS.LOGIN, loginRequest);
      const data = response.data;
      if (data && data.mfa_required) {
        setChallengeToken(data.challenge_token);
        setStage("mfa");
        setCode("");
        setTrustDevice(false);
      } else {
        completeLogin(data);
      }
    } catch (err: any) {
      if (err.response?.status === 428) {
        const d = err.response?.data;
        setEnroll({
          enrollToken: d.enroll_token,
          secret: d.secret,
          otpauthUri: d.otpauth_uri,
          issuer: d.issuer,
          account: d.account,
        });
        setStage("enroll");
        setCode("");
      } else {
        handleError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e: any) => {
    e.preventDefault();
    if (!code) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(API_URLS.MFA_VERIFY, {
        challenge_token: challengeToken,
        code,
        trust_device: trustDevice,
      });
      completeLogin(response.data);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError("Demasiadas tentativas. Aguarde e tente novamente.");
      } else if (err.response?.status === 401) {
        setError("Código inválido. Tente novamente.");
      } else {
        setError("Não foi possível verificar o código. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async (
    tokenField: "challenge_token" | "enroll_token",
    token: string,
  ) => {
    if (resendCooldown > 0) return;
    setError(null);
    try {
      await api.post(API_URLS.MFA_RESEND_OTP, { [tokenField]: token });
      setResendCooldown(30);
      const timer = setInterval(() => {
        setResendCooldown((v) => {
          if (v <= 1) {
            clearInterval(timer);
            return 0;
          }
          return v - 1;
        });
      }, 1000);
    } catch {
      setError("Não foi possível enviar o código. Tente novamente.");
    }
  };

  const handleConfirmEnroll = async (e: any) => {
    e.preventDefault();
    if (!enroll || !code) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(API_URLS.MFA_ENROLL_CONFIRM, {
        enroll_token: enroll.enrollToken,
        code,
      });
      completeLogin(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError("Código inválido. Tente novamente.");
      } else {
        setError("Não foi possível confirmar. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    if (!enroll) return;
    try {
      await navigator.clipboard.writeText(enroll.secret);
    } catch {
      // clipboard write failure is non-critical
    }
  };

  useEffect(() => {
    if (location.pathname === "/logout") {
      // @ts-ignore
      api.clearToken();
      setProfile(null);
      setError(null);
      setErrorTitle(null);
      navigate("/login", { replace: true });
    }
  }, [location.pathname, navigate, setProfile]);

  if (isAuthenticated()) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded shadow-md w-96 text-center">
          <h2 className="text-2xl font-bold mb-6">Login bem-sucedido</h2>
          <p className="mb-4">Já se encontra autenticado.</p>
          <div className="flex gap-4">
            <Button
              color="primary"
              className="flex-1"
              style={{ width: "50%" }}
              onClick={() =>
                navigate(location.state?.from ?? "/", { replace: true })
              }
            >
              Ir para a aplicação.
            </Button>
            <Button
              color="danger"
              className="flex-1"
              style={{ width: "50%" }}
              onClick={() => {
                // @ts-ignore
                api.clearToken();
                setProfile(null);
                setError(null);
                setErrorTitle(null);
              }}
            >
              Sair
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (errorTitle) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded shadow-md w-96 text-center">
          <p className="text-2xl text-red-500 font-bold mb-6">{errorTitle}</p>
          {error ? (
            <p className="text-red-500 mb-4">{error}</p>
          ) : (
            <p className="mb-4">You must be logged in to access this page.</p>
          )}
          <Button
            color="primary"
            fullwidth
            onClick={() => {
              setError(null);
              setErrorTitle(null);
            }}
          >
            Voltar ao Login
          </Button>
        </div>
      </div>
    );
  }

  if (stage === "mfa") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <form
          onSubmit={handleVerifyMfa}
          className="bg-white p-8 rounded shadow-md w-96"
        >
          <h2 className="text-2xl font-bold mb-2 text-center">
            Verificação em dois passos
          </h2>
          <p className="text-sm text-gray-600 mb-6 text-center">
            Introduza o código de 6 dígitos da sua aplicação de autenticação.
          </p>
          {error && <p className="text-red-500 mb-4">{error}</p>}
          <div className="control mb-6">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Código de 6 dígitos"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCode(e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && code.length === 6) {
                  handleVerifyMfa(e);
                }
              }}
              required
              className="input text-center tracking-widest"
            />
          </div>
          <label className="flex items-center gap-2 mb-4 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTrustDevice(e.target.checked)
              }
              className="rounded"
            />
            Confiar neste dispositivo por 1 dia
          </label>
          <Button
            submit
            type="submit"
            color="primary"
            fullwidth
            disabled={loading || code.length !== 6}
            onClick={handleVerifyMfa}
          >
            <KeyRoundIcon className="w-4 h-4 mr-2" />
            {loading ? "A verificar…" : "Verificar"}
          </Button>
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => handleResendOtp("challenge_token", challengeToken)}
              disabled={resendCooldown > 0}
              className="text-sm underline text-gray-500 hover:text-gray-700"
            >
              {resendCooldown > 0
                ? `Reenviar código por email (${resendCooldown}s)`
                : "Não tem acesso? Enviar código por email"}
            </button>
          </div>
          <div className="text-center mt-3">
            <button
              type="button"
              onClick={() => {
                setStage("credentials");
                setError(null);
              }}
              className="text-sm underline text-gray-400 hover:text-gray-600"
            >
              ← Voltar ao login
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (stage === "enroll" && enroll) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <form
          onSubmit={handleConfirmEnroll}
          className="bg-white p-8 rounded shadow-md w-96"
        >
          <h2 className="text-2xl font-bold mb-2 text-center">
            Ativar verificação em dois passos
          </h2>
          <p className="text-sm text-gray-600 mb-4 text-center">
            A sua conta requer verificação em dois passos. Digitalize o código
            QR abaixo com a sua aplicação de autenticação (ex.: Google
            Authenticator, 1Password) ou introduza a chave manualmente.
          </p>
          {error && <p className="text-red-500 mb-4">{error}</p>}
          {enroll.otpauthUri && (
            <div className="flex justify-center mb-4">
              <div className="bg-white p-3 rounded border border-gray-200">
                <QRCodeSVG value={enroll.otpauthUri} size={160} level="M" />
              </div>
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chave secreta
            </label>
            <div className="flex gap-2">
              <code className="flex-1 p-2 bg-gray-100 rounded text-xs break-all">
                {enroll.secret}
              </code>
              <button
                type="button"
                onClick={copySecret}
                className="text-sm underline text-gray-500 hover:text-gray-700"
              >
                Copiar
              </button>
            </div>
            <a
              href={enroll.otpauthUri}
              className="text-xs underline text-gray-400 hover:text-gray-600 mt-1 inline-block"
            >
              Abrir diretamente na aplicação
            </a>
          </div>
          <div className="control mb-6">
            <input
              type="text"
              inputMode="numeric"
              placeholder="Código de confirmação"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCode(e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && code.length === 6) {
                  handleConfirmEnroll(e);
                }
              }}
              required
              className="input text-center tracking-widest"
            />
          </div>
          <Button
            submit
            type="submit"
            color="primary"
            fullwidth
            disabled={loading || code.length !== 6}
            onClick={handleConfirmEnroll}
          >
            {loading ? "A confirmar…" : "Confirmar e entrar"}
          </Button>
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() =>
                handleResendOtp("enroll_token", enroll.enrollToken)
              }
              disabled={resendCooldown > 0}
              className="text-sm underline text-gray-500 hover:text-gray-700"
            >
              {resendCooldown > 0
                ? `Reenviar código por email (${resendCooldown}s)`
                : "Não tem acesso à app? Enviar código por email"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <form
        onSubmit={handleCredentials}
        className="bg-white p-8 rounded shadow-md w-96"
      >
        <h2 className="text-2xl font-bold mb-6 text-center">Login</h2>
        {error && <p className="text-red-500 mb-4">{error}</p>}
        <div className="control mb-4">
          <input
            type="text"
            placeholder="Username"
            autoFocus
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setLoginRequest({ ...loginRequest, username: e.target.value });
            }}
            value={loginRequest.username}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                loginRequest.username &&
                loginRequest.password
              ) {
                handleCredentials(e);
              }
            }}
            required
            className="input"
          />
        </div>
        <div className="control mb-6">
          <input
            type="password"
            placeholder="Password"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setLoginRequest({ ...loginRequest, password: e.target.value });
            }}
            value={loginRequest.password}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                loginRequest.username &&
                loginRequest.password
              ) {
                handleCredentials(e);
              }
            }}
            required
            className="input"
          />
        </div>
        <Button
          submit
          type="submit"
          color="primary"
          fullwidth
          disabled={loading}
          onClick={handleCredentials}
        >
          <LogInIcon className="w-4 h-4 mr-2" />
          {loading ? "A entrar…" : "Login"}
        </Button>
      </form>
    </div>
  );
};

export default LoginPage;
